import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { useAuth } from '../context/AuthContext';
import { formatCurrency } from '../utils/currency';
import toast from 'react-hot-toast';
import {
  ArrowPathIcon,
  BanknotesIcon,
  EyeIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  PlusIcon
} from '@heroicons/react/24/outline';

const today = new Date().toISOString().split('T')[0];

const emptyCar = {
  name: '',
  make: '',
  model: '',
  year: '',
  licensePlate: '',
  vin: '',
  color: '',
  engineNumber: '',
  registrationNumber: '',
  purchaseDate: '',
  purchaseAmount: '',
  currentValue: '',
  status: 'active',
  notes: '',
  insurance: {
    provider: '',
    policyNumber: '',
    expirationDate: '',
    premium: ''
  },
  registration: {
    expirationDate: '',
    renewalFee: ''
  }
};

const emptyCarExpense = {
  type: 'fuel',
  amount: '',
  date: today,
  description: '',
  vendor: '',
  mileage: '',
  receiptNumber: ''
};

const formatDate = (value) => {
  if (!value) return 'N/A';
  return new Date(value).toLocaleDateString();
};

const toInputDate = (value) => {
  if (!value) return '';
  return new Date(value).toISOString().split('T')[0];
};

const cleanPayload = (data) => {
  const cleanObject = (value) => {
    if (!value || typeof value !== 'object') return value;
    const result = {};
    Object.entries(value).forEach(([key, childValue]) => {
      if (typeof childValue === 'string') {
        const trimmed = childValue.trim();
        if (trimmed) result[key] = trimmed;
      } else if (childValue !== undefined && childValue !== null && childValue !== '') {
        result[key] = childValue;
      }
    });
    return result;
  };

  const payload = {};
  Object.entries(data).forEach(([key, value]) => {
    if (value && typeof value === 'object' && !(value instanceof Date)) {
      const nested = cleanObject(value);
      if (Object.keys(nested).length) payload[key] = nested;
    } else if (typeof value === 'string') {
      const trimmed = value.trim();
      if (trimmed) payload[key] = trimmed;
    } else if (value !== undefined && value !== null && value !== '') {
      payload[key] = value;
    }
  });
  return payload;
};

const ModalShell = ({ title, children, onClose, wide = false }) => (
  <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
    <div className={`relative top-8 mx-auto p-5 border w-11/12 ${wide ? 'max-w-5xl' : 'md:w-3/4 lg:w-1/2'} shadow-lg rounded-md bg-white`}>
      <div className="flex items-center justify-between mb-5">
        <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">
          &times;
        </button>
      </div>
      {children}
    </div>
  </div>
);

const StatCard = ({ label, value, tone = 'text-gray-900' }) => (
  <div className="card p-4">
    <p className="text-sm font-medium text-gray-500">{label}</p>
    <p className={`mt-1 text-2xl font-semibold ${tone}`}>{value}</p>
  </div>
);

const Cars = () => {
  const { canAccess, hasRole } = useAuth();
  const navigate = useNavigate();
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ search: '', status: '' });
  const [modalType, setModalType] = useState(null);
  const [selectedCar, setSelectedCar] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const canManageCars = hasRole(['admin', 'accounts']);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting }
  } = useForm({ defaultValues: emptyCar });

  const {
    register: registerExpense,
    handleSubmit: handleExpenseSubmit,
    reset: resetExpense,
    formState: { errors: expenseErrors, isSubmitting: isSavingExpense }
  } = useForm({ defaultValues: emptyCarExpense });

  const fetchCars = useCallback(async () => {
    if (!canAccess('cars')) return;

    try {
      setLoading(true);
      const params = new URLSearchParams({ limit: '100' });
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });

      const response = await api.get(`/api/cars?${params.toString()}`);
      setCars(response.data.cars || []);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to fetch cars');
    } finally {
      setLoading(false);
    }
  }, [canAccess, filters]);

  useEffect(() => {
    fetchCars();
  }, [fetchCars]);

  const stats = useMemo(() => {
    return cars.reduce(
      (acc, car) => {
        acc.total += 1;
        if (car.status === 'active') acc.active += 1;
        if (car.status === 'under_maintenance') acc.maintenance += 1;
        acc.expenses += Number(car.totalExpenses || 0);
        return acc;
      },
      { total: 0, active: 0, maintenance: 0, expenses: 0 }
    );
  }, [cars]);

  const carToForm = (car) => ({
    name: car.name || '',
    make: car.make || '',
    model: car.model || '',
    year: car.year || '',
    licensePlate: car.licensePlate || '',
    vin: car.vin || '',
    color: car.color || '',
    engineNumber: car.engineNumber || '',
    registrationNumber: car.registrationNumber || '',
    purchaseDate: toInputDate(car.purchaseDate),
    purchaseAmount: car.purchaseAmount ?? '',
    currentValue: car.currentValue ?? '',
    status: car.status || 'active',
    notes: car.notes || '',
    insurance: {
      provider: car.insurance?.provider || '',
      policyNumber: car.insurance?.policyNumber || '',
      expirationDate: toInputDate(car.insurance?.expirationDate),
      premium: car.insurance?.premium ?? ''
    },
    registration: {
      expirationDate: toInputDate(car.registration?.expirationDate),
      renewalFee: car.registration?.renewalFee ?? ''
    }
  });

  const fetchCarDetails = async (carId) => {
    try {
      setDetailLoading(true);
      const response = await api.get(`/api/cars/${carId}`);
      setSelectedCar(response.data.car);
      return response.data.car;
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to fetch car details');
      return null;
    } finally {
      setDetailLoading(false);
    }
  };

  const openModal = async (type, car = null) => {
    setModalType(type);
    setSelectedCar(car);

    if (type === 'create') {
      reset(emptyCar);
      return;
    }

    if (type === 'edit' && car) {
      reset(carToForm(car));
      return;
    }

    if (type === 'expense' && car) {
      resetExpense(emptyCarExpense);
      return;
    }

    if (type === 'view' && car) {
      await fetchCarDetails(car._id);
    }
  };

  const closeModal = () => {
    setModalType(null);
    setSelectedCar(null);
  };

  const onSubmit = async (data) => {
    const payload = cleanPayload(data);

    try {
      if (modalType === 'create') {
        await api.post('/api/cars', payload);
        toast.success('Car created');
      } else {
        await api.put(`/api/cars/${selectedCar._id}`, payload);
        toast.success('Car updated');
      }

      closeModal();
      fetchCars();
    } catch (error) {
      const firstValidation = error.response?.data?.errors?.[0]?.msg;
      toast.error(firstValidation || error.response?.data?.message || 'Failed to save car');
    }
  };

  const onExpenseSubmit = async (data) => {
    try {
      await api.post(`/api/cars/${selectedCar._id}/expense`, cleanPayload(data));
      toast.success('Car expense added');
      closeModal();
      fetchCars();
    } catch (error) {
      const firstValidation = error.response?.data?.errors?.[0]?.msg;
      toast.error(firstValidation || error.response?.data?.message || 'Failed to add car expense');
    }
  };

  const resetFilters = () => {
    setFilters({ search: '', status: '' });
  };

  const statusBadge = (status) => {
    switch (status) {
      case 'active':
        return 'status-approved';
      case 'under_maintenance':
        return 'status-pending';
      case 'sold':
      case 'scrapped':
        return 'status-rejected';
      default:
        return 'bg-gray-100 text-gray-800 px-2 py-1 rounded-full text-xs font-medium';
    }
  };

  const expenseTypeLabel = (type) => type?.replace('_', ' ') || 'other';

  if (!canAccess('cars')) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">You don't have permission to access this page.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Cars</h1>
          <p className="text-gray-600">Manage company vehicles, documents, and running costs</p>
        </div>
        {canManageCars && (
          <button onClick={() => openModal('create')} className="btn-primary flex items-center justify-center">
            <PlusIcon className="h-4 w-4 mr-2" />
            Add Car
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <StatCard label="Vehicles" value={stats.total} />
        <StatCard label="Active" value={stats.active} tone="text-green-600" />
        <StatCard label="Under Maintenance" value={stats.maintenance} tone="text-yellow-600" />
        <StatCard label="Vehicle Expenses" value={formatCurrency(stats.expenses)} tone="text-red-600" />
      </div>

      <div className="card p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="md:col-span-2 relative">
            <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              type="text"
              value={filters.search}
              onChange={(event) => setFilters((prev) => ({ ...prev, search: event.target.value }))}
              className="input-field !pl-11"
              placeholder="Search name, make, model, license plate"
            />
          </div>
          <select
            value={filters.status}
            onChange={(event) => setFilters((prev) => ({ ...prev, status: event.target.value }))}
            className="input-field"
          >
            <option value="">All status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="under_maintenance">Under Maintenance</option>
            <option value="sold">Sold</option>
            <option value="scrapped">Scrapped</option>
          </select>
          <button onClick={resetFilters} className="btn-secondary flex items-center justify-center">
            <ArrowPathIcon className="h-4 w-4 mr-2" />
            Reset
          </button>
        </div>
      </div>

      <div className="card">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Vehicle</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Plate / Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Insurance / Registration</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Expenses</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="5" className="px-6 py-8 text-center">
                    <div className="spinner mx-auto"></div>
                  </td>
                </tr>
              ) : cars.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-8 text-center text-gray-500">No vehicles found</td>
                </tr>
              ) : (
                cars.map((car) => (
                  <tr key={car._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="font-medium text-gray-900">{car.name}</div>
                      <div className="text-sm text-gray-500">{[car.make, car.model, car.year].filter(Boolean).join(' ') || 'No make/model'}</div>
                      {car.color && <div className="text-xs text-gray-400">{car.color}</div>}
                    </td>
                    <td className="px-6 py-4">
                      <div className="font-medium text-gray-900">{car.licensePlate}</div>
                      <span className={statusBadge(car.status)}>{car.status?.replace('_', ' ')}</span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      <div>Insurance: {formatDate(car.insurance?.expirationDate)}</div>
                      <div>Registration: {formatDate(car.registration?.expirationDate)}</div>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="font-semibold text-gray-900">{formatCurrency(car.totalExpenses)}</div>
                      <div className="text-xs text-gray-500">{car.expenses?.length || 0} entries</div>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex justify-end space-x-2">
                        <button onClick={() => navigate(`/cars/${car._id}`)} className="text-primary-600 hover:text-primary-900" title="View vehicle statement">
                          <EyeIcon className="h-5 w-5" />
                        </button>
                        {canManageCars && (
                          <>
                            <button onClick={() => openModal('edit', car)} className="text-yellow-600 hover:text-yellow-900" title="Edit vehicle">
                              <PencilIcon className="h-5 w-5" />
                            </button>
                            <button onClick={() => openModal('expense', car)} className="text-green-600 hover:text-green-900" title="Add expense">
                              <BanknotesIcon className="h-5 w-5" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {(modalType === 'create' || modalType === 'edit') && (
        <ModalShell title={modalType === 'create' ? 'Add Car' : 'Edit Car'} onClose={closeModal} wide>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="label">Vehicle Name</label>
                <input {...register('name', { required: 'Car name is required' })} className="input-field" />
                {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>}
              </div>
              <div>
                <label className="label">License Plate</label>
                <input {...register('licensePlate', { required: 'License plate is required' })} className="input-field" />
                {errors.licensePlate && <p className="mt-1 text-sm text-red-600">{errors.licensePlate.message}</p>}
              </div>
              <div>
                <label className="label">Status</label>
                <select {...register('status')} className="input-field">
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="under_maintenance">Under Maintenance</option>
                  <option value="sold">Sold</option>
                  <option value="scrapped">Scrapped</option>
                </select>
              </div>
              <div>
                <label className="label">Make</label>
                <input {...register('make')} className="input-field" />
              </div>
              <div>
                <label className="label">Model</label>
                <input {...register('model')} className="input-field" />
              </div>
              <div>
                <label className="label">Year</label>
                <input {...register('year')} type="number" className="input-field" />
              </div>
              <div>
                <label className="label">Color</label>
                <input {...register('color')} className="input-field" />
              </div>
              <div>
                <label className="label">VIN</label>
                <input {...register('vin')} className="input-field" />
              </div>
              <div>
                <label className="label">Engine Number</label>
                <input {...register('engineNumber')} className="input-field" />
              </div>
              <div>
                <label className="label">Registration Number</label>
                <input {...register('registrationNumber')} className="input-field" />
              </div>
              <div>
                <label className="label">Purchase Date</label>
                <input {...register('purchaseDate')} type="date" className="input-field" />
              </div>
              <div>
                <label className="label">Purchase Amount</label>
                <input {...register('purchaseAmount')} type="number" step="0.01" className="input-field" />
              </div>
              <div>
                <label className="label">Current Value</label>
                <input {...register('currentValue')} type="number" step="0.01" className="input-field" />
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="card p-4">
                <h4 className="font-semibold text-gray-900 mb-3">Insurance</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="label">Provider</label>
                    <input {...register('insurance.provider')} className="input-field" />
                  </div>
                  <div>
                    <label className="label">Policy Number</label>
                    <input {...register('insurance.policyNumber')} className="input-field" />
                  </div>
                  <div>
                    <label className="label">Expiration Date</label>
                    <input {...register('insurance.expirationDate')} type="date" className="input-field" />
                  </div>
                  <div>
                    <label className="label">Premium</label>
                    <input {...register('insurance.premium')} type="number" step="0.01" className="input-field" />
                  </div>
                </div>
              </div>
              <div className="card p-4">
                <h4 className="font-semibold text-gray-900 mb-3">Registration</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="label">Expiration Date</label>
                    <input {...register('registration.expirationDate')} type="date" className="input-field" />
                  </div>
                  <div>
                    <label className="label">Renewal Fee</label>
                    <input {...register('registration.renewalFee')} type="number" step="0.01" className="input-field" />
                  </div>
                </div>
              </div>
            </div>

            <div>
              <label className="label">Notes</label>
              <textarea {...register('notes')} rows={3} className="input-field" />
            </div>
            <div className="flex justify-end space-x-3">
              <button type="button" onClick={closeModal} className="btn-secondary">Cancel</button>
              <button type="submit" disabled={isSubmitting} className="btn-primary">
                {isSubmitting ? 'Saving...' : modalType === 'create' ? 'Create Car' : 'Update Car'}
              </button>
            </div>
          </form>
        </ModalShell>
      )}

      {modalType === 'expense' && selectedCar && (
        <ModalShell title={`Add Expense - ${selectedCar.name}`} onClose={closeModal}>
          <form onSubmit={handleExpenseSubmit(onExpenseSubmit)} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="label">Type</label>
                <select {...registerExpense('type', { required: 'Type is required' })} className="input-field">
                  <option value="fuel">Fuel</option>
                  <option value="maintenance">Maintenance</option>
                  <option value="repair">Repair</option>
                  <option value="insurance">Insurance</option>
                  <option value="registration">Registration</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <label className="label">Amount</label>
                <input
                  {...registerExpense('amount', { required: 'Amount is required', min: { value: 0.01, message: 'Amount must be positive' } })}
                  type="number"
                  step="0.01"
                  className="input-field"
                />
                {expenseErrors.amount && <p className="mt-1 text-sm text-red-600">{expenseErrors.amount.message}</p>}
              </div>
              <div>
                <label className="label">Date</label>
                <input {...registerExpense('date', { required: 'Date is required' })} type="date" className="input-field" />
              </div>
              <div>
                <label className="label">Mileage</label>
                <input {...registerExpense('mileage')} type="number" className="input-field" />
              </div>
              <div>
                <label className="label">Vendor</label>
                <input {...registerExpense('vendor')} className="input-field" />
              </div>
              <div>
                <label className="label">Receipt Number</label>
                <input {...registerExpense('receiptNumber')} className="input-field" />
              </div>
            </div>
            <div>
              <label className="label">Description</label>
              <textarea {...registerExpense('description', { required: 'Description is required' })} rows={3} className="input-field" />
              {expenseErrors.description && <p className="mt-1 text-sm text-red-600">{expenseErrors.description.message}</p>}
            </div>
            <div className="flex justify-end space-x-3">
              <button type="button" onClick={closeModal} className="btn-secondary">Cancel</button>
              <button type="submit" disabled={isSavingExpense} className="btn-primary">
                {isSavingExpense ? 'Saving...' : 'Add Expense'}
              </button>
            </div>
          </form>
        </ModalShell>
      )}

      {modalType === 'view' && selectedCar && (
        <ModalShell title={selectedCar.name} onClose={closeModal} wide>
          {detailLoading ? (
            <div className="py-12 flex justify-center">
              <div className="spinner"></div>
            </div>
          ) : (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                <div><span className="font-medium text-gray-500">Plate:</span> {selectedCar.licensePlate}</div>
                <div><span className="font-medium text-gray-500">Make/Model:</span> {[selectedCar.make, selectedCar.model, selectedCar.year].filter(Boolean).join(' ') || 'N/A'}</div>
                <div><span className="font-medium text-gray-500">Status:</span> {selectedCar.status?.replace('_', ' ')}</div>
                <div><span className="font-medium text-gray-500">Insurance Expiry:</span> {formatDate(selectedCar.insurance?.expirationDate)}</div>
                <div><span className="font-medium text-gray-500">Registration Expiry:</span> {formatDate(selectedCar.registration?.expirationDate)}</div>
                <div><span className="font-medium text-gray-500">Total Expenses:</span> {formatCurrency(selectedCar.totalExpenses)}</div>
              </div>

              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Expense History</h4>
                {(selectedCar.expenses || []).length === 0 ? (
                  <p className="text-sm text-gray-500">No vehicle expenses yet.</p>
                ) : (
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {[...selectedCar.expenses].reverse().map((expense) => (
                      <div key={expense._id} className="border border-gray-200 rounded-md p-3">
                        <div className="flex justify-between gap-3">
                          <div>
                            <p className="font-medium text-gray-900 capitalize">{expenseTypeLabel(expense.type)}</p>
                            <p className="text-sm text-gray-600">{expense.description}</p>
                          </div>
                          <p className="font-semibold text-gray-900">{formatCurrency(expense.amount)}</p>
                        </div>
                        <p className="text-xs text-gray-500">
                          {formatDate(expense.date)} by {expense.addedBy?.name || 'N/A'}
                          {expense.vendor ? ` | ${expense.vendor}` : ''}
                          {expense.mileage ? ` | ${expense.mileage} km` : ''}
                          {expense.receiptNumber ? ` | ${expense.receiptNumber}` : ''}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </ModalShell>
      )}
    </div>
  );
};

export default Cars;
